import { pick } from 'lodash';

import { isFree } from '@zapier/design-system-revenue/src/utils';
import {
  ChangeType,
  Plan,
  PlanV2,
} from '@zapier/design-system-revenue/src/types';

type GetChangePlanTypeProps = {
  fromPlan: Plan | PlanV2 | undefined | null;
  toPlan: Plan | PlanV2;
};

const getValueScore = (plan?: Plan | PlanV2 | null) => {
  if (!plan || plan.amount === 0) {
    return 0;
  }

  // These are values are ones where "smaller is better"
  const inverseFeatures = ['zapsRunInterval'];

  /**
   * We want to normalise all the number based features (# tasks, zaps, etc.)
   * to a value between 0 and 1 so that all features are weighted equally.
   */
  const featuresToNormalise = pick(plan, ['tasks', 'zaps', ...inverseFeatures]);

  // These features are boolean flags - weighted as 1 (true) or 0 (false)
  const regularFeatures = pick(plan, [
    'hasAutoReplay',
    'hasPaths',
    'hasMeteredBilling',
    'hasMultiStepZaps',
    'hasPremiumApps',
    'hasTeams',
    'allowCompanies',
  ]);

  const min = 0;

  // one hundred thousand dollars, a number large enough that we're not likely to charge a customer
  const max = 10000000;

  const normalisedFeatures = Object.entries(featuresToNormalise).reduce(
    (obj, [key, value]) => ({
      ...obj,
      [key]: (Number(value) - min) / (max - min),
    }),
    {}
  );

  const features = { ...regularFeatures, ...normalisedFeatures };

  return Object.entries(features).reduce(
    (acc, [key, value]) =>
      inverseFeatures.includes(key)
        ? acc - Number(value || 0)
        : acc + Number(value || 0),
    0
  );
};

/**
 * Return one of 'downgrade', 'upgrade', or 'change'.
 * If plan value is less than current plan value, return 'downgrade'.
 * If greater return 'upgrade'. If plan is same value as current plan, return 'change'.
 */
export const getChangePlanType = ({
  fromPlan,
  toPlan,
}: GetChangePlanTypeProps): ChangeType => {
  // Switching from one free plan to another is an upgrade
  if (isFree(fromPlan) && isFree(toPlan) && fromPlan?.id !== toPlan.id) {
    return 'upgrade';
  }

  /**
   * Company features trump all other criteria. Without this explicit check, a change from the
   * Team 2M plan to the Company 100,000 Plan will register as a downgrade because the difference
   * in task limit is so large.
   */
  if (toPlan.type === 'Company' && fromPlan?.type !== 'Company') {
    return 'upgrade';
  }

  const currentValueScore = getValueScore(fromPlan);
  const newPlanValueScore = getValueScore(toPlan);

  return newPlanValueScore > currentValueScore
    ? 'upgrade'
    : newPlanValueScore < currentValueScore
    ? 'downgrade'
    : 'change';
};
